import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Loader2,
  Terminal,
  FileJson,
  Table as TableIcon,
  Database,
  Download,
  FileIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { TreeView } from '@/components/ui/tree-view';
import { useState, useMemo } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getNodeDefinition } from '@/utils/nodeDefinitions';
import { NodeIcon } from '@/components/ui/node-icon';
import { cn } from '@/lib/utils';
import { CompactModeToggle } from './CompactModeToggle';
import { OutputPanelSkeleton } from './LoadingSkeletons';
import { ExportMenu } from './ExportMenu';

/**
 * Detects binary/base64 file content in output and returns file info
 */
function detectFileContent(output: any): {
  hasFile: boolean;
  fileName?: string;
  mimeType?: string;
  size?: number;
  contentBase64?: string;
} {
  if (!output || typeof output !== 'object') {
    return { hasFile: false };
  }

  // Check direct properties
  const contentBase64 = output.contentBase64 || output.content_base64 || output.base64Content;
  const fileName = output.fileName || output.filename || output.name || output.file_name;
  const mimeType = output.mimeType || output.mime_type || output.contentType || output.content_type || 'application/octet-stream';
  const size = output.size || output.fileSize || output.file_size;

  if (contentBase64 && typeof contentBase64 === 'string' && contentBase64.length > 100) {
    // Verify it looks like base64 (contains only valid base64 chars)
    const base64Regex = /^[A-Za-z0-9+/=]+$/;
    if (base64Regex.test(contentBase64.replace(/\s/g, ''))) {
      return { hasFile: true, fileName, mimeType, size, contentBase64 };
    }
  }

  // Check nested output property (for unified format responses)
  if (output.output && typeof output.output === 'object') {
    return detectFileContent(output.output);
  }

  return { hasFile: false };
}

/**
 * Download base64 content as a file
 */
function downloadBase64File(contentBase64: string, fileName: string, mimeType: string) {
  try {
    // Decode base64 to binary
    const byteCharacters = atob(contentBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Downloaded ${fileName}`);
  } catch (error) {
    console.error('Failed to download file:', error);
    toast.error('Failed to download file');
  }
}

/**
 * Format file size in human readable format
 */
function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

interface NodeOutputPanelProps {
  output: any;
  error: string | null;
  isLoading: boolean;
  nodeType?: string;
  nodeName?: string;
}

export function NodeOutputPanel({ output, error, isLoading, nodeType, nodeName }: NodeOutputPanelProps) {
  const [activeTab, setActiveTab] = useState<'schema' | 'json' | 'table' | 'debug'>('schema');
  const [isCompact, setIsCompact] = useState(false);

  // Display unified format response as-is: { success, output, error, executionTime, logs, metrics }
  // Show the full structure to match the Input Panel behavior
  let displayOutput = output;

  // If output has unified format structure, use the error from it (overrides prop error)
  if (output && typeof output === 'object' && 'success' in output && 'error' in output) {
    if (output.error && !error) {
      error = output.error;
    }
  }

  // Detect if output contains downloadable file content
  const fileInfo = useMemo(() => detectFileContent(displayOutput), [displayOutput]);

  const handleDownloadFile = () => {
    if (fileInfo.hasFile && fileInfo.contentBase64) {
      downloadBase64File(
        fileInfo.contentBase64,
        fileInfo.fileName || 'download',
        fileInfo.mimeType || 'application/octet-stream'
      );
    }
  };

  const handleCopyJson = () => {
    if (displayOutput) {
      navigator.clipboard.writeText(JSON.stringify(displayOutput, null, 2));
      toast.success('JSON copied to clipboard');
    }
  };

  const handleCopyAll = () => {
    if (displayOutput) {
      navigator.clipboard.writeText(JSON.stringify(displayOutput, null, 2));
      toast.success('Data copied to clipboard');
    }
  };

  const handleCopyCurl = () => {
    const debugInfo = displayOutput?._debug;
    if (debugInfo?.request?.curlCommand) {
      navigator.clipboard.writeText(debugInfo.request.curlCommand);
      toast.success('Curl command copied to clipboard');
    }
  };

  // Show error state
  if (error && !isLoading) {
    return (
      <div className="h-full p-3">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-semibold mb-2">Test Failed</div>
            <div className="text-sm">{error}</div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!displayOutput && !isLoading) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center">
          <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-40" />
          <h3 className="text-sm font-medium text-foreground mb-2">
            No output yet
          </h3>
          <p className="text-xs text-muted-foreground">
            Click "Test" to run this node
          </p>
        </div>
      </div>
    );
  }

  // Show skeleton when loading (both first time and re-execution)
  if (isLoading) {
    return <OutputPanelSkeleton />;
  }

  const hasDebugInfo = displayOutput?._debug;
  const debugInfo = displayOutput?._debug;

  const renderSchemaView = () => (
    <div className={cn("bg-muted/30 rounded-lg p-2 border border-border/50 overflow-hidden", isCompact && "p-1")}>
      <TreeView
        data={displayOutput}
        defaultExpanded={isCompact ? 1 : 2}
        showTypes={!isCompact}
        showCopyButtons={!isCompact}
        showPath={!isCompact}
        className="min-h-[200px] max-w-full"
      />
    </div>
  );

  // Helper to check if string looks like base64 binary content
  const isBase64Content = (value: string): boolean => {
    if (value.length < 100) return false;
    // Base64 should NOT contain common text characters like newlines, commas, spaces
    const sample = value.substring(0, 200);
    const hasTextIndicators = /[\n\r,\s:;{}[\]<>]/.test(sample);
    if (hasTextIndicators) return false;
    // Base64 pattern check
    return /^[A-Za-z0-9+/]+=*$/.test(value.substring(0, 100));
  };

  // Sanitize output for JSON display by truncating large strings
  const sanitizeForDisplay = (obj: any, maxStringLength = 500): any => {
    if (obj === null || obj === undefined) return obj;

    if (typeof obj === 'string') {
      if (obj.length > maxStringLength) {
        const byteSize = new Blob([obj]).size;
        const formatSize = (bytes: number) => {
          if (bytes < 1024) return `${bytes} B`;
          if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
          return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
        };
        // Check if it looks like base64 (using stricter check)
        if (isBase64Content(obj)) {
          const estimatedBinarySize = Math.floor(byteSize * 0.75);
          return `[Binary content: ~${formatSize(estimatedBinarySize)} encoded as ${formatSize(byteSize)} base64]`;
        }
        // For text content, show preview
        const preview = obj.substring(0, maxStringLength).replace(/\n/g, '\\n');
        return `${preview}... [${formatSize(byteSize)} total]`;
      }
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForDisplay(item, maxStringLength));
    }

    if (typeof obj === 'object') {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = sanitizeForDisplay(value, maxStringLength);
      }
      return result;
    }

    return obj;
  };

  const renderJsonView = () => {
    // Sanitize output to truncate large strings before stringifying
    const sanitizedOutput = sanitizeForDisplay(displayOutput);
    const jsonString = JSON.stringify(sanitizedOutput, null, 2);

    // Simple syntax highlighting
    const highlightedJson = jsonString
      .replace(/"([^"]+)":/g, '<span class="text-blue-400">"$1"</span>:')
      .replace(/: "([^"]*)"/g, ': <span class="text-orange-400">"$1"</span>')
      .replace(/: (\d+)/g, ': <span class="text-green-400">$1</span>')
      .replace(/: (true|false)/g, ': <span class="text-purple-400">$1</span>')
      .replace(/: null/g, ': <span class="text-gray-400">null</span>');

    return (
      <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
        <pre
          className="text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-[600px]"
          dangerouslySetInnerHTML={{ __html: highlightedJson }}
        />
      </div>
    );
  };

  const renderTableView = () => {
    if (!displayOutput || typeof displayOutput !== 'object') {
      return (
        <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
          <div className="text-xs text-muted-foreground">Not a table-compatible format</div>
        </div>
      );
    }

    // Try to find an array in the data
    let items: any[] = [];

    if (Array.isArray(displayOutput)) {
      items = displayOutput;
    } else if (displayOutput.items && Array.isArray(displayOutput.items)) {
      items = displayOutput.items;
    } else if (displayOutput.data && Array.isArray(displayOutput.data)) {
      items = displayOutput.data;
    } else if (displayOutput.results && Array.isArray(displayOutput.results)) {
      items = displayOutput.results;
    } else if (displayOutput.rows && Array.isArray(displayOutput.rows)) {
      items = displayOutput.rows;
    } else {
      // If no array found, treat the object as a single row
      items = [displayOutput];
    }

    if (items.length === 0) {
      return (
        <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
          <div className="text-xs text-muted-foreground">No data to display</div>
        </div>
      );
    }

    // Filter out non-object items
    const objectItems = items.filter(item => item && typeof item === 'object' && !Array.isArray(item));
    
    if (objectItems.length === 0) {
      return (
        <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
          <div className="text-xs text-muted-foreground">
            Data contains {items.length} items but they are not objects that can be displayed in a table
          </div>
        </div>
      );
    }

    // Get all unique keys from all items
    const allKeys = new Set<string>();
    objectItems.forEach(item => {
      Object.keys(item).forEach(key => allKeys.add(key));
    });
    
    const keys = Array.from(allKeys);

    return (
      <div className="bg-muted/30 rounded-lg p-4 border border-border/50 overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              {keys.map((key) => (
                <th key={key} className="text-left p-2 font-semibold text-foreground">
                  {key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {objectItems.slice(0, 100).map((item: any, idx: number) => (
              <tr key={idx} className="border-b border-border hover:bg-muted/30">
                {keys.map((key) => {
                  const cellValue = item[key];
                  // Truncate large strings in table cells
                  const displayValue = typeof cellValue === 'string' && cellValue.length > 200
                    ? cellValue.substring(0, 200) + '...'
                    : cellValue;

                  return (
                    <td key={key} className="p-2 align-top">
                      {cellValue === null || cellValue === undefined ? (
                        <span className="text-muted-foreground italic">null</span>
                      ) : typeof cellValue === 'object' ? (
                        <pre className="text-xs font-mono max-w-xs overflow-x-auto">
                          {JSON.stringify(sanitizeForDisplay(cellValue, 200), null, 2)}
                        </pre>
                      ) : typeof cellValue === 'boolean' ? (
                        <span className={cellValue ? 'text-green-600' : 'text-red-600'}>
                          {String(cellValue)}
                        </span>
                      ) : typeof cellValue === 'number' ? (
                        <span className="font-mono">{cellValue}</span>
                      ) : (
                        <span className="break-words max-w-md block" title={typeof cellValue === 'string' && cellValue.length > 200 ? `${cellValue.length} characters` : undefined}>
                          {String(displayValue)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {objectItems.length > 100 && (
          <div className="text-xs text-muted-foreground mt-2 text-center p-2 bg-muted/30 rounded">
            Showing 100 of {objectItems.length} rows
          </div>
        )}
      </div>
    );
  };

  const renderDebugView = () => {
    if (!debugInfo) return null;

    return (
      <div className="space-y-4">
        {/* Request Details */}
        <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            Request Details
          </h3>
          <div className="space-y-2 text-xs">
            <div className="flex gap-2">
              <span className="font-semibold text-muted-foreground min-w-[80px]">Method:</span>
              <span className="font-mono">{debugInfo.request.method}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-semibold text-muted-foreground min-w-[80px]">URL:</span>
              <span className="font-mono break-all">{debugInfo.request.url}</span>
            </div>
            {debugInfo.request.queryParams && (
              <div className="flex gap-2">
                <span className="font-semibold text-muted-foreground min-w-[80px]">Query:</span>
                <span className="font-mono">{JSON.stringify(debugInfo.request.queryParams)}</span>
              </div>
            )}
            <div className="flex gap-2">
              <span className="font-semibold text-muted-foreground min-w-[80px]">Body:</span>
              <span className="font-mono">{debugInfo.request.hasBody ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>

        {/* Headers */}
        <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
          <h3 className="text-sm font-semibold mb-3">Headers</h3>
          <div className="space-y-1 text-xs font-mono">
            {Object.entries(debugInfo.request.headers || {}).map(([key, value]) => (
              <div key={key} className="flex gap-2">
                <span className="text-primary">{key}:</span>
                <span className="text-muted-foreground">{String(value)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Curl Command */}
        {debugInfo.request.curlCommand && (
          <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Curl Command</h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={handleCopyCurl}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy curl command</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all bg-background p-3 rounded border">
              {debugInfo.request.curlCommand}
            </pre>
            <p className="text-xs text-muted-foreground mt-2">
              💡 Copy this command to test the request in your terminal
            </p>
          </div>
        )}

        {/* Error Details (if present) */}
        {debugInfo.error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-3 text-destructive">Error Details</h3>
            <div className="space-y-2 text-xs">
              <div className="flex gap-2">
                <span className="font-semibold text-muted-foreground min-w-[80px]">Message:</span>
                <span>{debugInfo.error.message}</span>
              </div>
              {debugInfo.error.code && (
                <div className="flex gap-2">
                  <span className="font-semibold text-muted-foreground min-w-[80px]">Code:</span>
                  <span className="font-mono">{debugInfo.error.code}</span>
                </div>
              )}
              {debugInfo.error.responseStatus && (
                <div className="flex gap-2">
                  <span className="font-semibold text-muted-foreground min-w-[80px]">Status:</span>
                  <span className="font-mono">{debugInfo.error.responseStatus}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Get node definition for icon
  const nodeDef = nodeType ? getNodeDefinition(nodeType) : null;

  return (
    <div className="h-full w-full flex flex-col bg-muted/30 my-2 mx-2 rounded-lg shadow-inner border border-border/50 relative">
      {/* Header with OUTPUT label and tab buttons */}
      <div className="flex-shrink-0 border-b border-border bg-background/50 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">OUTPUT</h3>
          
          <div className="flex items-center gap-1">
            {/* Schema/JSON/Table/Debug Toggle Buttons */}
            <div className="flex items-center border rounded-md bg-background">
              <Button
                variant={activeTab === 'schema' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 text-xs rounded-r-none"
                onClick={() => setActiveTab('schema')}
              >
                Schema
              </Button>
              <Button
                variant={activeTab === 'json' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 text-xs rounded-none border-l border-r"
                onClick={() => setActiveTab('json')}
              >
                JSON
              </Button>
              <Button
                variant={activeTab === 'table' ? 'default' : 'ghost'}
                size="sm"
                className={cn(
                  "h-7 text-xs",
                  hasDebugInfo ? "rounded-none border-r" : "rounded-l-none"
                )}
                onClick={() => setActiveTab('table')}
              >
                Table
              </Button>
              {hasDebugInfo && (
                <Button
                  variant={activeTab === 'debug' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7 text-xs rounded-l-none"
                  onClick={() => setActiveTab('debug')}
                >
                  Debug
                </Button>
              )}
            </div>

            {/* Compact Mode Toggle */}
            <CompactModeToggle 
              isCompact={isCompact} 
              onToggle={() => setIsCompact(!isCompact)} 
            />
          </div>
        </div>
      </div>

      {/* Node Header with Icon and Copy/Export Buttons */}
      <div className="flex-shrink-0 border-b border-border bg-muted/50 px-3 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center w-6 h-6 rounded-md"
              style={{ backgroundColor: nodeDef?.bgColor || '#f1f5f9' }}
            >
              <NodeIcon
                icon={nodeDef?.icon || Database}
                iconType={nodeDef?.iconType}
                iconColor={nodeDef?.iconColor}
                color={nodeDef?.color}
                className="w-4 h-4"
              />
            </div>
            <span className="text-sm font-medium">{nodeName || 'Node Output'}</span>
          </div>
          
          <div className="flex items-center gap-1">
            {/* Export Menu */}
            <ExportMenu data={output} nodeName={nodeName} />
            
            {activeTab === 'schema' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={handleCopyAll}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Copy all data</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {activeTab === 'json' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={handleCopyJson}
                    >
                      <FileJson className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Copy JSON</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {activeTab === 'debug' && debugInfo?.request?.curlCommand && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={handleCopyCurl}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Copy curl command</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <ScrollAreaViewport>
          <div className="p-3">
            {/* File Download Card - shown when binary/base64 content is detected */}
            {fileInfo.hasFile && (
              <div className="mb-4 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/20">
                      <FileIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">
                        {fileInfo.fileName || 'Downloaded File'}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        {fileInfo.mimeType && <span>{fileInfo.mimeType}</span>}
                        {fileInfo.size && (
                          <>
                            <span className="text-muted-foreground/50">•</span>
                            <span>{formatFileSize(fileInfo.size)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={handleDownloadFile}
                    className="gap-2"
                    size="sm"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                </div>
              </div>
            )}

            {activeTab === 'schema' && renderSchemaView()}
            {activeTab === 'json' && renderJsonView()}
            {activeTab === 'table' && renderTableView()}
            {activeTab === 'debug' && renderDebugView()}
          </div>
        </ScrollAreaViewport>
        <ScrollAreaScrollbar orientation="vertical" />
      </ScrollArea>
    </div>
  );
}
